import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { FormStateContextProvider } from '|/context';
import AccordionStep, { AccordionStepData } from './accordion-step';
import HeightCoordinatorContext from './height-coordinator-context';
import { getPageEventContext } from '../../constants/page-event-mappings';

interface CheckoutAccordionProps {
  steps: AccordionStepData[];
  activeStepUrl: string | null;
  exitingStepUrl: string | null;
  onToggle: (url: string) => void;
  stepChildren: Record<string, React.ReactNode>;
  verificationStepUrls: Set<string>;
  isRefreshing: boolean;
}

const CheckoutAccordion: React.FC<CheckoutAccordionProps> = ({
  steps,
  activeStepUrl,
  exitingStepUrl,
  onToggle,
  stepChildren,
  verificationStepUrls,
  isRefreshing
}) => {
  const { reportLoading, requestStepTransition } = useContext(HeightCoordinatorContext);

  useEffect(() => {
    reportLoading('dashboard-refresh', isRefreshing);
  }, [isRefreshing, reportLoading]);

  const stepElRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const setStepRef = useCallback((url: string, el: HTMLDivElement | null) => {
    if (el) {
      stepElRefs.current.set(url, el);
    } else {
      stepElRefs.current.delete(url);
    }
  }, []);

  useEffect(() => {
    if (activeStepUrl === null) return;
    const resolvedUrl = verificationStepUrls.has(activeStepUrl) ? '_verification' : activeStepUrl;
    const activeIdx = steps.findIndex(s => s.url === resolvedUrl);
    if (activeIdx === -1) return;
    if (activeIdx === 0) {
      requestStepTransition(0);
      return;
    }
    const prevStepEl = stepElRefs.current.get(steps[activeIdx - 1].url);
    if (!prevStepEl) return;
    requestStepTransition(prevStepEl);
  }, [steps, activeStepUrl, verificationStepUrls, requestStepTransition]);

  const firstIncompleteIdx = steps.findIndex(s => !s.isComplete);
  const firstIncompleteUrl = firstIncompleteIdx >= 0 ? steps[firstIncompleteIdx].url : null;

  return (
    <div>
      {steps.map((step, idx) => {
        const isActiveStep =
          step.url === activeStepUrl ||
          (step.url === '_verification' &&
            activeStepUrl != null &&
            verificationStepUrls.has(activeStepUrl));

        const isExiting = step.url === exitingStepUrl;

        const isExpanded = isActiveStep;

        const isNextStep = step.url === firstIncompleteUrl;
        const isFuture = firstIncompleteIdx >= 0 && idx > firstIncompleteIdx;
        const isAccessible = !isFuture && (step.isComplete || step.url === firstIncompleteUrl);

        return (
          <FormStateContextProvider key={step.url} endLoadDebounceMs={200}>
            <AccordionStep
              stepRef={el => setStepRef(step.url, el)}
              step={step}
              isAccessible={isAccessible}
              isFuture={isFuture}
              isExpanded={isExpanded}
              pageEventScope={getPageEventContext(step.url)}
              isNextStep={isNextStep}
              isExiting={isExiting}
              onToggle={() => {
                onToggle(step.url);
              }}
            >
              {stepChildren[step.url]}
            </AccordionStep>
          </FormStateContextProvider>
        );
      })}
    </div>
  );
};

export default CheckoutAccordion;
